import {

  Dialog,

  DialogTitle,

  DialogContent,

  DialogActions,

  Button,

  FormControl,

  InputLabel,

  Select,

  MenuItem,

  Grid,

  CircularProgress,

  Box,

  TextField,

} from "@mui/material";

import React, { useEffect } from "react";

import { Component } from "../../../../types/component";

import { Airframe } from "../../../../types/airframe";

import { useAirframeAvailableSlots } from "../../../../hooks/useAirframeAvailableSlots";

import { getComponentTypeName } from "../../../../utils/componentTypeUtils";

import {

  ComponentSubAssemblyAttachInfo,

  isLandingGearCompatibleWithAirframe,
  findSlotIdByLocationCode,

} from "../../../../utils/landingGearAttachUtils";



type AttachFormState = {

  componentId: string;

  slotId: string;

};



type Props = {

  open: boolean;

  airframe: Airframe | null;

  components: Component[];

  form: AttachFormState;

  onChange: (f: AttachFormState) => void;

  onAttach: () => void;

  onClose: () => void;

  isAttaching?: boolean;

  templateSupportsSubAssemblies?: boolean;

  subAssemblyByComponentId?: Record<string, ComponentSubAssemblyAttachInfo>;

};



export function AttachComponentDialog({

  open,

  airframe: afm,

  components,

  form,

  onChange,

  onAttach,

  onClose,

  isAttaching = false,

  templateSupportsSubAssemblies = false,

  subAssemblyByComponentId = {},

}: Props) {

  const { slots, loading, fetchAvailableSlots } = useAirframeAvailableSlots();

  // Keep landing gear aligned to airframe family (CRJ/Embraer) or manufacturer.
  const filteredComponents = components.filter((c) =>
    isLandingGearCompatibleWithAirframe(c, afm?.manufacturer)
  );
  const selectedComponent = filteredComponents.find((c) => c.id === form.componentId);

  const selectedSubAssemblyInfo = form.componentId

    ? subAssemblyByComponentId[form.componentId.trim().toLowerCase()]

    : undefined;

  const lockPositionFromSubAssembly = Boolean(

    templateSupportsSubAssemblies && selectedSubAssemblyInfo?.landingGearType

  );



  let componentType = selectedComponent

    ? getComponentTypeName(selectedComponent.componentType)

    : undefined;



  useEffect(() => {

    if (open && componentType && afm?.id) {

      fetchAvailableSlots(afm.id, String(componentType));

    }

  }, [open, componentType, afm?.id, fetchAvailableSlots]);



  useEffect(() => {

    if (!open || !form.componentId || loading || slots.length === 0) {

      return;

    }



    if (!lockPositionFromSubAssembly && form.slotId) {

      return;

    }



    const landingGearType = selectedSubAssemblyInfo?.landingGearType;

    if (!landingGearType) return;



    const slotId = findSlotIdByLocationCode(slots, landingGearType);

    if (slotId && form.slotId !== slotId) {

      onChange({ componentId: form.componentId, slotId });

    }

  }, [

    open,

    form.componentId,

    form.slotId,

    loading,

    slots,

    lockPositionFromSubAssembly,

    selectedSubAssemblyInfo,

    onChange,

  ]);



  const selectedSlot = slots.find((slot) => slot.id === form.slotId);

  const positionDisplay = selectedSlot

    ? `${selectedSlot.locationCode} (Position ${selectedSlot.slotNumber})`

    : "";



  const positionFieldSx = {

    "& .MuiInputLabel-root": { color: "#fff" },

    "& .MuiInputLabel-root.Mui-focused": { color: "hsl(19 101% 51% / 1)" },

    "& .MuiOutlinedInput-root": {

      color: "#fff",

      "& fieldset": { borderColor: "#fff" },

      "&:hover fieldset": { borderColor: "#fff" },

      "&.Mui-focused fieldset": {

        borderColor: "hsl(19 101% 51% / 1)",

      },

      "& input": {

        color: "#fff",

        WebkitTextFillColor: "#fff",

      },

    },

  } as const;



  return (

    <Dialog

      open={open}

      onClose={onClose}

      fullWidth

      PaperProps={{

        sx: {

          backgroundColor: "#111",

          color: "#fff",

          border: "1px solid #333",

        },

      }}

    >

      <DialogTitle sx={{ color: "hsl(19 101% 51% / 1)" }}>

        Attach Assembly to {afm?.tailNumber}

      </DialogTitle>



      <DialogContent>

        <Grid container spacing={2} sx={{ mt: 1 }}>

          <Grid size={{ xs: 12, sm: 6 }}>

            <FormControl fullWidth required>

              <InputLabel required sx={{ color: "#fff" }}>Select Assembly</InputLabel>

              <Select

                value={

                  filteredComponents.some(c => c.id === form.componentId)

                    ? form.componentId

                    : ""

                }

                onChange={(e) =>

                  onChange({

                    componentId: e.target.value as string,

                    slotId: "",

                  })

                }

                sx={{

                  color: "#fff",

                  "& .MuiOutlinedInput-notchedOutline": {

                    borderColor: "#fff",

                  },

                  "&:hover .MuiOutlinedInput-notchedOutline": {

                    borderColor: "#fff",

                  },

                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {

                    borderColor: "hsl(19 101% 51% / 1)",

                    borderWidth: 1,

                  },

                  "& .MuiSvgIcon-root": {

                    color: "#fff",

                  },

                }}

              >

                {filteredComponents.map(c => (

                  <MenuItem value={c.id} key={c.id}>

                    {getComponentTypeName(c.componentType) || c.componentType} - {c.manufacturer} {c.model} (S/N: {c.serialNumber})

                  </MenuItem>

                ))}

              </Select>

            </FormControl>

          </Grid>



          <Grid size={{ xs: 12, sm: 6 }}>

            {lockPositionFromSubAssembly ? (

              <TextField

                fullWidth

                required

                label="Position"

                value={loading ? "" : positionDisplay}

                placeholder={loading ? "Loading Positions..." : ""}

                InputProps={{ readOnly: true }}

                InputLabelProps={{ shrink: true }}

                sx={positionFieldSx}

              />

            ) : (

              <FormControl fullWidth required disabled={!componentType || loading}>

                <InputLabel required sx={{ color: "#fff" }}>

                  Position

                </InputLabel>

                <Select

                  value={form.slotId}

                  onChange={(e) =>

                    onChange({ ...form, slotId: e.target.value as string })

                  }

                  sx={{

                    color: "#fff",

                    "& .MuiOutlinedInput-notchedOutline": {

                      borderColor: "#fff",

                    },

                    "&:hover .MuiOutlinedInput-notchedOutline": {

                      borderColor: "#fff",

                    },

                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {

                      borderColor: "hsl(19 101% 51% / 1)",

                      borderWidth: 1,

                    },

                    "& .MuiSvgIcon-root": {

                      color: "#fff",

                    },

                  }}

                >

                  {loading ? (

                    <MenuItem disabled>

                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>

                        <CircularProgress size={20} />

                        Loading Positions...

                      </Box>

                    </MenuItem>

                  ) : slots.length > 0 ? (

                    slots.map((slot) => (

                      <MenuItem key={slot.id} value={slot.id}>

                        {slot.locationCode} (Position {slot.slotNumber})

                      </MenuItem>

                    ))

                  ) : (

                    <MenuItem disabled>

                      {componentType ? "No available positions" : "Select an assembly first"}

                    </MenuItem>

                  )}

                </Select>

              </FormControl>

            )}

          </Grid>

        </Grid>

      </DialogContent>



      <DialogActions>

        <Button onClick={onClose} sx={{ color: "#fff" }} disabled={isAttaching}>

          Cancel

        </Button>

        <Button

          variant="contained"

          onClick={onAttach}

          disabled={isAttaching}

          startIcon={isAttaching ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : undefined}

          sx={{

            backgroundColor: "hsl(19 101% 51% / 1)",

            "&:hover": {

              backgroundColor: "hsl(19 101% 41% / 1)",

            },

          }}

        >

          {isAttaching ? "Attaching..." : "Attach"}

        </Button>

      </DialogActions>

    </Dialog>

  );

}


